// apps/spa/src/components/reminders/ReminderRowActions.tsx
// '⋮' DropdownMenu used by each row in the ReminderTable.
//
// Items:
//   * Post Now    — calls usePostNow().mutate({ id, channelName })
//   * Edit        — navigate to /reminders/:id/edit
//   * Delete      — calls onDelete(reminder.id) which raises into the parent
//                  RemindersList route to show DeleteReminderDialog.

import { useNavigate } from 'react-router';
import { MoreVertical, Send, Pencil, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePostNow } from '@/hooks/usePostNow';
import type { Reminder } from '@guardian/shared';

export interface ReminderRowActionsProps {
  reminder: Reminder;
  channelName: string;
  onDelete: (id: string) => void;
}

export function ReminderRowActions({
  reminder,
  channelName,
  onDelete,
}: ReminderRowActionsProps) {
  const navigate = useNavigate();
  const postNow = usePostNow();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Actions for ${reminder.name}`}
        className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <MoreVertical className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => postNow.mutate({ id: reminder.id, channelName })}
          disabled={postNow.isPending}
        >
          <Send className="size-4" /> Post Now
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate(`/reminders/${reminder.id}/edit`)}
        >
          <Pencil className="size-4" /> Edit
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onDelete(reminder.id)}>
          <Trash2 className="size-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
